import {match} from 'ts-pattern';

import type {AdvertisementPreflightIssue} from '@dms/api/sales';
import {mandatoryFieldsMap, MandatoryFieldMapValue} from '@dms/shared';

import {getSafely} from 'shared/web';

const vehicleSourceFields: Record<string, string> = {
  'vehicleData.vin': 'VIN',
  'vehicleData.registrationPlate': 'LICENSE_PLATE',
  'vehicleData.condition': 'CONDITION',
  'vehicleData.manufacturedOn': 'REAL_PRODUCTION_YEAR',
  'vehicleData.mileage': 'MILEAGE',
  'vehicleData.firstRegistration': 'FIRST_REGISTRATION',
  'vehicleData.firstRegistrationOn': 'FIRST_REGISTRATION',
  'vehicleData.exteriorColor.code': 'BODY_COLOR',
  'vehicleData.exteriorColor.value': 'BODY_COLOR',
  'vehicleData.exteriorColorSpecification': 'BODY_COLOR_TYPE',
  'vehicleData.originCountry': 'COUNTRY_OF_ORIGIN',
  'vehicleData.technicalInspectionValidUntil': 'TECHNICAL_INSPECTION_VALID_UNTIL',
  'vehicleData.lastServiceDate': 'LAST_SERVICE_DATE',
  'vehicleData.lastServiceMileage': 'LAST_SERVICE_MILEAGE',
  'vehicleData.warrantyDate': 'WARRANTY_UNTIL',
  'vehicleData.warrantyMileage': 'WARRANTY_MILEAGE',
  'vehicleData.inspections': 'TIRE_SIZE',
};

export type AdvertisingPreflightGroup = {
  kind: 'vehicle' | 'prices' | 'photos' | 'platform' | 'other';
  field?: MandatoryFieldMapValue;
  issues: AdvertisementPreflightIssue[];
};

export function groupAdvertisingPreflightIssues(issues: AdvertisementPreflightIssue[]) {
  const groups = new Map<string, AdvertisingPreflightGroup>();

  for (const issue of issues) {
    const paths = issue.sourcePaths.map((path) => path.replace(/^source\./, ''));
    const fields = paths.map((path) =>
      getSafely(mandatoryFieldsMap, getSafely(vehicleSourceFields, path) ?? '')
    );
    const kind = match({kind: issue.kind, paths})
      .returnType<AdvertisingPreflightGroup['kind']>()
      .when(
        ({kind, paths}) =>
          issue.reasonCode === 'inspection-data-unavailable' ||
          kind !== 'field' ||
          paths.length === 0,
        () => 'other'
      )
      .when(
        () =>
          fields.every(
            (item) =>
              item !== undefined && item.tab === fields[0]?.tab && item.href === fields[0]?.href
          ),
        () => 'vehicle'
      )
      .when(
        ({paths}) =>
          paths.every((path) =>
            /^(price\.|financingPrice\.|vehicleData\.sellingPrice$)/.test(path)
          ),
        () => 'prices'
      )
      .when(
        ({paths}) =>
          paths.every((path) =>
            /^(images(?:\.|$)|automarketpl\.(zdjecie|listaPozostalychZdjec))/.test(path)
          ),
        () => 'photos'
      )
      .when(
        ({paths}) => paths.every((path) => path.startsWith('automarketpl.')),
        () => 'platform'
      )
      .otherwise(() => 'other');
    const field = kind === 'vehicle' ? fields[0] : undefined;

    const key = `${kind}:${field?.href ?? ''}:${field?.tab ?? ''}`;
    const group = groups.get(key);
    if (group) {
      group.issues.push(issue);
    } else {
      groups.set(key, {kind, field, issues: [issue]});
    }
  }

  return [...groups.values()];
}
